import { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Users } from "lucide-react";
import LoadingSequence from "@/components/LoadingSequence";
import SEO from "@/components/SEO";
import { analyzeChannel, searchChannels } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import { formatCompact } from "@/lib/format";

// Detect if the query looks like a URL / handle / channel ID that we can
// resolve directly, versus a free-text channel name that needs a picker.
const isDirectChannelRef = (q) => {
  if (!q) return false;
  const s = q.trim();
  return (
    s.includes("youtube.com/") ||
    s.includes("youtu.be/") ||
    /^UC[\w-]{20,}$/.test(s) ||
    s.startsWith("@")
  );
};

export default function Analyze() {
  const location = useLocation();
  const navigate = useNavigate();
  const [url, setUrl] = useState(location.state?.url || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const debounceRef = useRef(null);

  const showPickerFor = useMemo(
    () => !isDirectChannelRef(url) && url.trim().length >= 2,
    [url],
  );

  useEffect(() => {
    if (!showPickerFor) {
      setSuggestions([]);
      setShowPicker(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSuggestLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchChannels(url.trim());
        setSuggestions(data.results || []);
        setShowPicker(true);
      } catch (e) {
        console.debug("channel search failed", e);
      } finally {
        setSuggestLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [url, showPickerFor]);

  const runByRef = async (targetUrl, meta = {}) => {
    const trimmed = (targetUrl || "").trim();
    if (!trimmed) {
      toast.error("Please enter a channel URL or name");
      return;
    }
    trackEvent("channel_search", { query: trimmed.slice(0, 200), ...meta });
    setLoading(true);
    setError(null);
    setShowPicker(false);
    try {
      const result = await analyzeChannel(trimmed);
      const slug =
        (result.stats?.custom_url || "").replace(/^@/, "").toLowerCase() ||
        result.channel_id;
      toast.success(result.from_cache ? "Loaded from cache" : "Analysis complete!");
      navigate(`/channel/${slug}`, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to analyze";
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    // If the query is a URL/handle/ID, resolve directly. If it's a name and
    // we have suggestions, pick the top one. Otherwise ask the user to click.
    if (isDirectChannelRef(url)) {
      runByRef(url);
      return;
    }
    if (suggestions.length > 0) {
      const top = suggestions[0];
      runByRef(`https://youtube.com/channel/${top.channel_id}`, {
        selected_from: "top_suggestion",
        channel_id: top.channel_id,
      });
      return;
    }
    // No suggestions yet — kick a search
    if (url.trim().length >= 2) {
      setShowPicker(true);
    } else {
      toast.error("Type at least 2 characters");
    }
  };

  const pickChannel = (c) => {
    setUrl(c.title);
    runByRef(`https://youtube.com/channel/${c.channel_id}`, {
      selected_from: "picker",
      channel_id: c.channel_id,
    });
  };

  useEffect(() => {
    if (location.state?.url) {
      runByRef(location.state.url);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  return (
    <div>
      <SEO
        title="Analyze YouTube Channel"
        description="Search a YouTube channel by URL or name. Get complete statistics, estimated revenue, engagement, growth and AI-powered recommendations."
        path="/analyze"
        keywords="analyze youtube channel, search youtube channel by name, youtube channel analyzer, youtube analytics tool, youtube channel report, youtube statistics"
      />
      {!loading && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <div className="overline">Step 1</div>
          <h1 className="mt-2 text-4xl font-black tracking-tight">Analyze a YouTube Channel</h1>
          <p className="mt-3 text-muted-foreground">
            Enter a <strong className="text-foreground">channel URL, @handle or the channel name</strong>. If you type a name we'll show a list of matching channels — just click the one you want.
          </p>
          <form onSubmit={onSubmit} className="mt-8 relative">
            <div className="flex gap-2 p-2 rounded-2xl bg-card border border-border">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search className="w-5 h-5 text-muted-foreground" />
                <Input
                  data-testid="analyze-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Paste a YouTube URL or type a channel name"
                  className="border-0 shadow-none focus-visible:ring-0 h-12 bg-transparent px-0"
                  autoComplete="off"
                />
              </div>
              <Button data-testid="analyze-submit" type="submit" className="rounded-xl bg-primary hover:bg-primary/90 h-12 px-6 font-bold">
                Analyze
              </Button>
            </div>

            {/* Channel picker dropdown */}
            {showPicker && showPickerFor && (
              <div
                data-testid="analyze-picker"
                className="absolute z-30 left-0 right-0 mt-2 bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
              >
                {suggestLoading && suggestions.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground">Searching channels…</div>
                )}
                {!suggestLoading && suggestions.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground">
                    No channels matched "<span className="font-semibold">{url}</span>".
                  </div>
                )}
                {suggestions.length > 0 && (
                  <>
                    <div className="px-4 py-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border/60">
                      {suggestions.length} matches — click to analyze
                    </div>
                    <ul className="max-h-[420px] overflow-auto divide-y divide-border/50">
                      {suggestions.map((c) => (
                        <li key={c.channel_id}>
                          <button
                            type="button"
                            data-testid={`picker-${c.channel_id}`}
                            onClick={() => pickChannel(c)}
                            className="w-full text-left px-4 py-3 hover:bg-secondary/50 transition-colors flex items-center gap-3"
                          >
                            {c.thumbnail ? (
                              <img
                                src={c.thumbnail}
                                alt={`${c.title} avatar`}
                                className="w-11 h-11 rounded-full object-cover shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-11 h-11 rounded-full bg-secondary shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold truncate">{c.title}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {c.handle && <span className="font-mono">{c.handle}</span>}
                                {c.handle && c.description && " · "}
                                {c.description}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xs font-mono flex items-center gap-1 text-muted-foreground justify-end">
                                <Users className="w-3 h-3" />
                                {c.hidden_subscribers ? "hidden" : formatCompact(c.subscribers)}
                              </div>
                              {c.country && (
                                <div className="text-[10px] text-muted-foreground/70 font-mono mt-0.5">{c.country}</div>
                              )}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </form>
          {error && (
            <div data-testid="analyze-error" className="mt-6 p-4 rounded-lg border border-destructive/40 bg-destructive/10 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>
      )}

      {loading && <LoadingSequence />}
    </div>
  );
}
